import { useMemo } from 'react';

import meanBy from 'lodash/meanBy';
import round from 'lodash/round';
import {
  ChartMetricsRow,
  ChartMetricsRowSkeleton,
} from '@cast/features-cluster-ui';

import { InventoryResourceMetricBox } from '../../../../_components/ui/InventoryResourceMetricBox';
import { chartDataSettings } from './_constants';
import { ResourceImpactChartDataKey, ResourceImpactChartItem } from './_types';

const colorMap: Record<ResourceImpactChartDataKey, string> = {
  originalReq: chartDataSettings.originalReq.color,
  req: chartDataSettings.req.color,
  usage: chartDataSettings.usage.color,
};

const titleMap: Record<ResourceImpactChartDataKey, string> = {
  originalReq: chartDataSettings.originalReq.title,
  req: chartDataSettings.req.title,
  usage: chartDataSettings.usage.title,
};

const METRIC_KEYS = Object.keys(
  chartDataSettings
) as ResourceImpactChartDataKey[];

type Props = {
  toggleMetric: (key: ResourceImpactChartDataKey) => void;
  isMetricDisabled: (key: ResourceImpactChartDataKey) => boolean;
  unit: string;
  data: ResourceImpactChartItem[] | undefined;
  isLoading?: boolean;
  testId: string;
};

export const ImpactAvgMetrics = ({
  data,
  toggleMetric,
  isMetricDisabled,
  unit,
  isLoading = false,
  testId,
}: Props) => {
  const averages = useMemo(() => {
    if (!data) {
      return;
    }

    return METRIC_KEYS.reduce((acc, key) => {
      acc[key] = round(meanBy(data, key), 2);
      return acc;
    }, {} as Record<ResourceImpactChartDataKey, number>);
  }, [data]);

  if (isLoading) {
    return (
      <ChartMetricsRowSkeleton
        alwaysRow
        gap={24}
        count={METRIC_KEYS.length}
        testId={`${testId}-skeleton`}
      />
    );
  }

  return (
    <ChartMetricsRow alwaysRow alignItems="center" gap={16} testId={testId}>
      {METRIC_KEYS.map((keyCast) => (
        <InventoryResourceMetricBox
          key={keyCast}
          testId={keyCast}
          dataKey={keyCast}
          value={averages?.[keyCast]}
          unit={unit}
          label={`Avg. ${chartDataSettings[keyCast].title}`}
          infoTooltip={chartDataSettings[keyCast].tooltipText}
          colorMap={colorMap}
          titleMap={titleMap}
          toggleMetric={toggleMetric}
          isMetricDisabled={isMetricDisabled}
        />
      ))}
    </ChartMetricsRow>
  );
};
